/**
 * TNK-130 phases D + E — wizard step 2: progress + review + confirm.
 *
 * Polls /api/imports/:id every 1s while enrichment is in flight, then
 * surfaces the editable review table. The operator can:
 *
 *   - Inline-edit any parsed field on any row (PATCH /items/:itemId).
 *   - Hit "Re-lookup" on a failed row to re-queue the enrichment worker.
 *   - Pick a batch bin + batch lot at the footer.
 *   - Confirm the import → POST /confirm → cards land in inventory.
 *   - Discard the draft → DELETE /imports/:id.
 *
 * Confirm is disabled until enrichment is settled (no pending /
 * enriching rows). Failed rows don't block confirm — the operator
 * either fixes them with inline edit (status flips to "fixed") or
 * accepts the import without those images.
 */
import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  AlertCircle,
  ArrowUp,
  CheckCircle2,
  Edit3,
  Grid3x3,
  Image as ImageIcon,
  Loader2,
  RefreshCw,
  Rows3,
  Trash2,
  X,
} from "lucide-react";

import { BackButton } from "@/components/BackButton";
import { FIGMA } from "@/components/import/figmaStyles";
import { ImportStepper } from "@/components/import/ImportStepper";
import { Modal } from "@/components/Modal";
import { PrimaryButton } from "@/components/PrimaryButton";
import { type CardCondition } from "@/context/AppContext";
import { imageUrl } from "@/features/imageUpload";
import {
  deleteImportDraft,
  deleteImportItem,
  getImportDraft,
  patchImportItem,
  relookupImportItem,
  swapAllImportItemImages,
  swapImportItemImages,
  type ApiImportDraftItem,
  type DraftState,
} from "@/features/importsApi";

export const Route = createFileRoute("/tools/imports/$id/")({
  component: ImportDraftPage,
});

const POLL_INTERVAL_MS = 1000;

const CONDITIONS: CardCondition[] = ["NM", "LP", "MP", "HP", "Damaged"];

type FinishOption = "Normal" | "Holo" | "Reverse Holo" | "Premium";
const FINISH_OPTIONS: FinishOption[] = [
  "Normal",
  "Holo",
  "Reverse Holo",
  "Premium",
];

interface ParsedShape {
  name?: string;
  setName?: string;
  setCode?: string;
  cardNumber?: string;
  condition?: string;
  quantity?: number;
  marketPrice?: number;
  costBasis?: number;
  variant?: string;
  rarity?: string;
  game?: string;
  notes?: string;
  /** CardFinish | undefined. UI surfaces "Normal" when undefined; promote
   *  helper writes undefined / null as the no-finish case. */
  finish?: string | null;
}

function ImportDraftPage() {
  const navigate = useNavigate();
  const { id } = Route.useParams();

  const [state, setState] = useState<DraftState | null>(null);
  const [error, setError] = useState<string>("");
  const [editingItem, setEditingItem] = useState<ApiImportDraftItem | null>(
    null,
  );

  // TNK-134 — Grid vs Table view. Initial default is decided once
  // (`null` while undecided) based on row count: Grid for ≤50, Table
  // for >50. Operator can override either way; the override persists
  // for the lifetime of this page mount.
  const [view, setView] = useState<"grid" | "table" | null>(null);

  // TNK-134 phase C — Swap-all in-flight flag + discard typed-confirm.
  // Discard takes a draft to zero, so a typed-confirm matches the
  // rollback shape: click once → input row → type DISCARD → confirm.
  const [swapAllInFlight, setSwapAllInFlight] = useState(false);
  const [discardPhase, setDiscardPhase] = useState<"idle" | "confirm">("idle");
  const [discardTyped, setDiscardTyped] = useState("");
  // TNK-217 — bulk swap-all is a cheap, whole-draft-inverting misclick, so gate
  // it behind a confirm and offer a one-click Undo right after (swap-all is its
  // own inverse). `undoSwapAll` shows the Undo affordance until another action.
  const [confirmSwapAll, setConfirmSwapAll] = useState(false);
  const [undoSwapAll, setUndoSwapAll] = useState(false);

  // Poll while enrichment is in flight.
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = async () => {
      try {
        const next = await getImportDraft(id);
        if (cancelled) return;
        setState(next);
        const inFlight =
          next.draft.status === "enriching" ||
          next.draft.status === "parsing" ||
          next.counters.pending > 0 ||
          next.counters.enriching > 0;
        if (inFlight) timer = setTimeout(tick, POLL_INTERVAL_MS);
      } catch (err) {
        if (!cancelled)
          setError((err as Error).message ?? "Couldn't load the draft.");
      }
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [id]);

  const counters = state?.counters;
  const draft = state?.draft;
  const items = state?.items ?? [];
  const isCommitted = draft?.status === "committed";

  // TNK-134 phase B: once the draft flips to committed (either from a
  // local confirm or from a poll that saw another tab commit it),
  // redirect to the dedicated Results route. URL semantics carry the
  // lifecycle: `/$id` = "I'm working" / `/$id/results` = "done."
  useEffect(() => {
    if (!isCommitted) return;
    void navigate({
      to: "/tools/imports/$id/results",
      params: { id },
      replace: true,
    });
  }, [isCommitted, id, navigate]);

  const inFlight = !!(
    draft &&
    counters &&
    (draft.status === "enriching" ||
      draft.status === "parsing" ||
      counters.pending > 0 ||
      counters.enriching > 0)
  );

  // Stable CSV-row order. A previous version sorted failed-first, which
  // made rows jump as their status changed during polling — and the
  // re-flow snapped the operator's scroll position back to the top.
  // Order-by-createdAt keeps every row anchored to its original visual
  // slot; status pills + the counter card highlight the rows that need
  // attention without requiring a layout shift.
  const sortedItems = useMemo(
    () =>
      [...items].sort(
        (a, b) =>
          a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id),
      ),
    [items],
  );

  const imagesAttached = items.filter((it) => it.imageFrontRef).length;

  const handleEdit = async (
    item: ApiImportDraftItem,
    patch: Partial<ParsedShape>,
  ): Promise<void> => {
    setError("");
    try {
      const next = await patchImportItem(
        id,
        item.id,
        patch as Record<string, unknown>,
      );
      setState((prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((it) => (it.id === next.id ? next : it)),
              counters: recountFromItems(
                prev.items.map((it) => (it.id === next.id ? next : it)),
              ),
            }
          : prev,
      );
    } catch (err) {
      setError((err as Error).message ?? "Edit failed.");
    }
  };

  const handleRelookup = async (item: ApiImportDraftItem) => {
    setError("");
    try {
      const next = await relookupImportItem(id, item.id);
      setState((prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((it) => (it.id === next.id ? next : it)),
              counters: recountFromItems(
                prev.items.map((it) => (it.id === next.id ? next : it)),
              ),
            }
          : prev,
      );
    } catch (err) {
      setError((err as Error).message ?? "Re-lookup failed.");
    }
  };

  // TNK-140 phase B: the actual confirm action moved to /$id/review.
  // Here we just hand off when the operator is happy with photos.
  const handleContinue = () => {
    void navigate({
      to: "/tools/imports/$id/review",
      params: { id },
    });
  };

  const handleDiscard = async () => {
    setError("");
    try {
      await deleteImportDraft(id);
      void navigate({ to: "/tools/imports/new" });
    } catch (err) {
      setError((err as Error).message ?? "Discard failed.");
    }
  };

  // TNK-134: pick a default view once the row count is known. Grid for
  // small batches (≤50), Table for big ones — keeps the polished tile
  // layout for the cases it shines on and the dense table for 400-row
  // ETB dumps.
  useEffect(() => {
    if (view !== null) return;
    if (!counters) return;
    setView(counters.total <= 50 ? "grid" : "table");
  }, [view, counters]);

  const handleDelete = async (item: ApiImportDraftItem) => {
    setError("");
    // Optimistic — drop the row locally, restore on failure.
    const prevState = state;
    setState((prev) =>
      prev
        ? {
            ...prev,
            items: prev.items.filter((it) => it.id !== item.id),
            counters: recountFromItems(
              prev.items.filter((it) => it.id !== item.id),
            ),
          }
        : prev,
    );
    try {
      await deleteImportItem(id, item.id);
    } catch (err) {
      setState(prevState);
      setError((err as Error).message ?? "Delete failed.");
    }
  };

  const handleSwapImages = async (item: ApiImportDraftItem) => {
    setError("");
    try {
      const next = await swapImportItemImages(id, item.id);
      setState((prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((it) => (it.id === next.id ? next : it)),
            }
          : prev,
      );
    } catch (err) {
      setError((err as Error).message ?? "Swap failed.");
    }
  };

  // `isUndo` distinguishes the Undo press (reverts the prior swap) from a fresh
  // swap — swap-all is its own inverse, so Undo just runs it again.
  const runSwapAll = async (isUndo: boolean) => {
    setConfirmSwapAll(false);
    setError("");
    setSwapAllInFlight(true);
    try {
      const result = await swapAllImportItemImages(id);
      // Server-authoritative refetch — bulk swap touched every item.
      const next = await getImportDraft(id);
      setState(next);
      if (result.swapped === 0) {
        setError("Nothing to swap — no rows have both front + back images.");
        setUndoSwapAll(false);
      } else {
        // Offer Undo after a forward swap; clear it once an Undo has reverted.
        setUndoSwapAll(!isUndo);
      }
    } catch (err) {
      setError((err as Error).message ?? "Swap all failed.");
    } finally {
      setSwapAllInFlight(false);
    }
  };

  if (error && !state) {
    return (
      <section className="flex h-full flex-col space-y-4">
        <BackButton fallback="/tools/imports/new" />
        <ErrorBanner message={error} />
      </section>
    );
  }
  if (!state || !draft || !counters) {
    return (
      <section className="flex h-full flex-col items-center justify-center py-12 text-muted-foreground">
        <Loader2 size={20} className="mx-auto animate-spin" />
        <p className="mt-3 text-sm">Loading draft…</p>
      </section>
    );
  }

  // TNK-134 phase B: while the redirect-on-committed effect is in
  // flight we'd briefly render the wizard with `isCommitted=true`.
  // Short-circuit that with a loading splash so the operator never
  // sees a half-state.
  if (isCommitted) {
    return (
      <section className="flex h-full flex-col items-center justify-center py-12 text-muted-foreground">
        <Loader2 size={20} className="mx-auto animate-spin" />
        <p className="mt-3 text-sm">Opening results…</p>
      </section>
    );
  }

  const progress =
    counters.total === 0
      ? 0
      : Math.round(
          ((counters.done + counters.failed + counters.fixed) /
            counters.total) *
            100,
        );

  return (
    // Section shell, full-width body — matches the rest of /tools/*.
    <section className="flex h-full flex-col space-y-6 pb-24">
      <BackButton fallback="/tools/imports/new" />

      <ImportStepper currentStep="upload" importId={id} />

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1
            className="text-2xl"
            style={{
              fontFamily: FIGMA.fontHeading,
              fontWeight: 700,
              color: FIGMA.fg,
              letterSpacing: "0.02em",
            }}
          >
            Import draft
          </h1>
          <p className="mt-1 text-sm" style={{ color: FIGMA.muted }}>
            {draft.sourceFilename ?? "Pasted CSV"} · {draft.sourceFormat} ·{" "}
            {draft.totalRows} row{draft.totalRows === 1 ? "" : "s"} ·{" "}
            <span style={{ fontFamily: FIGMA.fontMono, color: FIGMA.dim }}>
              {draft.id}
            </span>
          </p>
        </div>
        {discardPhase === "idle" ? (
          <button
            type="button"
            onClick={() => setDiscardPhase("confirm")}
            className="flex items-center gap-1.5 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/20"
          >
            <Trash2 size={12} />
            Discard
          </button>
        ) : (
          <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-2">
            <span className="text-[11px] text-muted-foreground">
              Type{" "}
              <span className="font-mono font-bold text-foreground">
                DISCARD
              </span>{" "}
              to confirm
            </span>
            <input
              type="text"
              value={discardTyped}
              onChange={(e) => setDiscardTyped(e.target.value)}
              placeholder="DISCARD"
              className="w-24 rounded border border-border bg-background px-2 py-1 text-xs"
              autoFocus
            />
            <button
              type="button"
              onClick={() => {
                setDiscardPhase("idle");
                setDiscardTyped("");
              }}
              className="rounded-md border border-border bg-card px-2 py-1 text-[11px] font-medium hover:bg-muted"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleDiscard()}
              disabled={discardTyped.trim().toUpperCase() !== "DISCARD"}
              className="flex items-center gap-1 rounded-md bg-destructive px-2 py-1 text-[11px] font-medium text-destructive-foreground hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Trash2 size={11} />
              Discard draft
            </button>
          </div>
        )}
      </div>

      {error && <ErrorBanner message={error} />}

      {/* Progress card — sticky so the operator never loses sight of it
          while scrolling the review table. z-10 keeps it above row
          hover backgrounds; backdrop-blur softens row text bleeding
          through on long lists. */}
      <div className="sticky top-2 z-10 space-y-4 rounded-lg border border-border bg-card/95 p-6 shadow-md backdrop-blur">
        <div className="flex items-center gap-3">
          {inFlight ? (
            <Loader2 size={20} className="animate-spin text-primary" />
          ) : (
            <CheckCircle2 size={20} className="text-success" />
          )}
          <div className="flex-1">
            <h2 className="text-sm font-semibold text-foreground">
              {inFlight
                ? "Processing in the background"
                : "Enrichment complete"}
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              {inFlight
                ? "You can close this tab — server keeps working and the draft will be waiting."
                : "Review the rows below, then assign a bin and lot to confirm the import."}
            </p>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {counters.done + counters.failed + counters.fixed} of{" "}
              {counters.total} rows processed
            </span>
            <span className="font-mono">{progress}%</span>
          </div>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-5">
          <Counter label="Done" value={counters.done} tone="success" />
          <Counter label="Fixed" value={counters.fixed} tone="info" />
          <Counter label="Enriching" value={counters.enriching} tone="info" />
          <Counter label="Pending" value={counters.pending} tone="muted" />
          <Counter label="Failed" value={counters.failed} tone="danger" />
        </div>

        <div className="flex items-center gap-2 border-t border-border pt-3 text-xs text-muted-foreground">
          <ImageIcon size={14} />
          <span>
            {imagesAttached} of {counters.total} photos attached
          </span>
        </div>
      </div>

      {/* Review section — Grid view (Figma tile layout) by default for
          small batches, Table view (dense scan) for larger ones.
          Operator can override either way. */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-foreground">
              Review {sortedItems.length} row
              {sortedItems.length === 1 ? "" : "s"}
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              {view === "grid"
                ? "Edit, swap front↔back, or remove any tile. Tiles with no photo are still committable."
                : "Failed rows surface first. Edit any field inline, then re-run enrichment or just commit the fix as-is."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setConfirmSwapAll(true)}
              disabled={swapAllInFlight || sortedItems.length === 0}
              title="Swap front and back on every row that has both images"
              className="flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
            >
              {swapAllInFlight ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <RefreshCw size={12} />
              )}
              Swap all
            </button>
            {undoSwapAll && !swapAllInFlight && (
              <button
                type="button"
                onClick={() => void runSwapAll(true)}
                title="Undo the last Swap all (swaps front↔back back)"
                className="flex items-center gap-1.5 rounded-md border border-warning/40 bg-warning/10 px-2.5 py-1 text-xs font-medium text-warning transition-colors hover:bg-warning/20"
              >
                <RefreshCw size={12} /> Undo swap
              </button>
            )}
            {view && <ViewToggle view={view} onChange={setView} />}
          </div>
        </div>

        {sortedItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border bg-card/40 py-12 text-center">
            <ImageIcon size={28} className="text-muted-foreground/40" />
            <div>
              <p className="text-sm font-medium text-foreground">
                No rows in this draft
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Every row was deleted. Discard the draft and re-upload, or
                cancel and add rows back.
              </p>
            </div>
            <Link
              to="/tools/imports/new"
              className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90"
            >
              Upload another CSV
            </Link>
          </div>
        ) : view === "grid" ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
            {sortedItems.map((item, i) => (
              <ImportTile
                key={item.id}
                item={item}
                index={i}
                committed={false}
                onEdit={() => setEditingItem(item)}
                onRelookup={() => void handleRelookup(item)}
                onSwapImages={() => void handleSwapImages(item)}
                onDelete={() => void handleDelete(item)}
              />
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className="w-full text-xs">
              <thead className="bg-muted/30 text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Front</th>
                  <th className="px-3 py-2 text-left">Back</th>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left">Set</th>
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Cond</th>
                  <th className="px-3 py-2 text-left">Variant</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-right">Price</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sortedItems.map((item) => (
                  <ReviewRow
                    key={item.id}
                    item={item}
                    committed={false}
                    onEdit={() => setEditingItem(item)}
                    onRelookup={() => void handleRelookup(item)}
                    onDelete={() => void handleDelete(item)}
                    onVariantChange={(finish) =>
                      void handleEdit(item, {
                        finish: finish === "Normal" ? null : finish,
                      })
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editingItem && (
        <EditRowModal
          item={editingItem}
          onClose={() => setEditingItem(null)}
          onSave={async (patch) => {
            await handleEdit(editingItem, patch);
            setEditingItem(null);
          }}
        />
      )}

      <BackToTopButton />

      <Modal
        open={confirmSwapAll}
        onClose={() => setConfirmSwapAll(false)}
        ariaLabel="Confirm swap all front and back"
        panelClassName="max-w-sm rounded-lg border border-border bg-card p-5"
      >
        <h2 className="text-sm font-semibold text-foreground">
          Swap front ↔ back on every row?
        </h2>
        <p className="mt-2 text-xs text-muted-foreground">
          This flips front and back on all{" "}
          {items.filter((it) => it.imageFrontRef && it.imageBackRef).length}{" "}
          two-sided rows in this draft. You can Undo right after.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setConfirmSwapAll(false)}
            className="rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void runSwapAll(false)}
            className="rounded-md border border-border bg-foreground px-3 py-1.5 text-xs font-medium text-background hover:opacity-90"
          >
            Swap all
          </button>
        </div>
      </Modal>

      <ContinueBar
        rows={items.length}
        disabled={inFlight || items.length === 0}
        waitingForEnrichment={inFlight}
        failedCount={counters.failed}
        onContinue={handleContinue}
      />
    </section>
  );
}

/** Floating "back to top" button — appears once the operator has
 *  scrolled past ~400px so they have a one-click escape from a long
 *  review table. Hidden at the top of the page so it doesn't crowd the
 *  layout for short imports. */
function BackToTopButton() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (!visible) return null;
  return (
    <button
      type="button"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Back to top"
      className="fixed bottom-6 right-6 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-opacity hover:opacity-90"
    >
      <ArrowUp size={18} />
    </button>
  );
}

function recountFromItems(items: ApiImportDraftItem[]) {
  const c = {
    total: items.length,
    pending: 0,
    enriching: 0,
    done: 0,
    failed: 0,
    fixed: 0,
  };
  for (const it of items) {
    switch (it.enrichmentStatus) {
      case "pending":
        c.pending += 1;
        break;
      case "enriching":
        c.enriching += 1;
        break;
      case "done":
        c.done += 1;
        break;
      case "failed":
        c.failed += 1;
        break;
      case "fixed":
        c.fixed += 1;
        break;
    }
  }
  return c;
}

function Counter({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "success" | "info" | "muted" | "danger";
}) {
  const toneClass: Record<typeof tone, string> = {
    success: "border-success/30 bg-success/10 text-success",
    info: "border-primary/30 bg-primary/10 text-primary",
    muted: "border-border bg-muted/30 text-muted-foreground",
    danger: "border-destructive/30 bg-destructive/10 text-destructive",
  };
  return (
    <div className={`rounded-md border px-2 py-1.5 ${toneClass[tone]}`}>
      <div className="font-mono text-base font-bold">{value}</div>
      <div className="text-[10px] uppercase tracking-wider">{label}</div>
    </div>
  );
}

function TableThumb({ src }: { src: string | null }) {
  if (src) {
    return (
      <img
        src={src}
        alt=""
        className="h-12 w-9 rounded border border-border object-cover"
      />
    );
  }
  return (
    <div className="flex h-12 w-9 items-center justify-center rounded border border-dashed border-border text-muted-foreground/40">
      <ImageIcon size={14} />
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending: "border-border bg-muted/30 text-muted-foreground",
    enriching: "border-primary/30 bg-primary/10 text-primary",
    done: "border-success/30 bg-success/10 text-success",
    failed: "border-destructive/30 bg-destructive/10 text-destructive",
    fixed: "border-warning/30 bg-warning/10 text-warning",
  };
  return (
    <span
      className={`inline-block rounded-sm border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${
        styles[status] ?? styles.pending
      }`}
    >
      {status}
    </span>
  );
}

function ReviewRow({
  item,
  committed,
  onEdit,
  onRelookup,
  onDelete,
  onVariantChange,
}: {
  item: ApiImportDraftItem;
  committed: boolean;
  onEdit: () => void;
  onRelookup: () => void;
  onDelete: () => void;
  onVariantChange: (finish: FinishOption) => void;
}) {
  const p = item.parsed as ParsedShape;
  const frontThumb = item.imageFrontRef
    ? imageUrl(item.imageFrontRef, "thumb")
    : null;
  const backThumb = item.imageBackRef
    ? imageUrl(item.imageBackRef, "thumb")
    : null;
  // Re-lookup is enabled on anything that didn't finish cleanly OR
  // finished with a partial-success warning (e.g. front attached, back
  // timed out — status is "done" but lookupError carries the back
  // failure so the operator can retry).
  const showRelookup =
    item.enrichmentStatus === "failed" ||
    item.enrichmentStatus === "pending" ||
    (item.enrichmentStatus === "done" && !!item.lookupError);
  // True "failed" rows surface in red; partial-success warnings on
  // otherwise-done rows surface in amber so the row reads as committable
  // but with a recoverable note.
  const errorTone =
    item.enrichmentStatus === "done" ? "warning" : "destructive";

  // Surface what the row was identified as so the operator can spot a
  // parse miss at a glance. "Complete" = name + set + number all present;
  // anything missing flips to a warning chip.
  const matchComplete = !!(p.name && p.setName && p.cardNumber);
  const matchLabel = matchComplete
    ? `Matched: ${p.game ?? "Pokemon"} · ${p.setName} · #${p.cardNumber}`
    : "Incomplete match — edit to fill in set + number";

  const currentFinish: FinishOption =
    p.finish === "Holo" || p.finish === "Reverse Holo" || p.finish === "Premium"
      ? (p.finish as FinishOption)
      : "Normal";

  return (
    <tr className="hover:bg-muted/20">
      <td className="px-3 py-2 align-top">
        <StatusPill status={item.enrichmentStatus} />
        {item.lookupError && (
          <div
            className={`mt-1 max-w-[180px] truncate text-[10px] ${
              errorTone === "warning" ? "text-warning" : "text-destructive"
            }`}
            title={item.lookupError}
          >
            {item.lookupError}
          </div>
        )}
      </td>
      <td className="px-3 py-2 align-top">
        <TableThumb src={frontThumb} />
      </td>
      <td className="px-3 py-2 align-top">
        <TableThumb src={backThumb} />
      </td>
      <td className="px-3 py-2 align-top">
        <div className="font-medium text-foreground">{p.name ?? "—"}</div>
        <div
          className={`mt-0.5 flex items-center gap-1 text-[10px] ${
            matchComplete ? "text-success" : "text-warning"
          }`}
          title={matchLabel}
        >
          {matchComplete ? (
            <CheckCircle2 size={10} />
          ) : (
            <AlertCircle size={10} />
          )}
          <span className="max-w-[220px] truncate">{matchLabel}</span>
        </div>
      </td>
      <td className="px-3 py-2 align-top">
        <div className="text-foreground">{p.setName ?? "—"}</div>
        {p.setCode && (
          <div className="text-[10px] text-muted-foreground">{p.setCode}</div>
        )}
      </td>
      <td className="px-3 py-2 align-top">{p.cardNumber ?? "—"}</td>
      <td className="px-3 py-2 align-top">{p.condition ?? "NM"}</td>
      <td className="px-3 py-2 align-top">
        <select
          value={currentFinish}
          onChange={(e) => onVariantChange(e.target.value as FinishOption)}
          className="rounded border border-border bg-background px-1.5 py-1 text-[11px]"
          aria-label={`Variant for ${p.name ?? "row"}`}
        >
          {FINISH_OPTIONS.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
      </td>
      <td className="px-3 py-2 text-right align-top">{p.quantity ?? 1}</td>
      <td className="px-3 py-2 text-right align-top font-mono">
        ${(p.marketPrice ?? 0).toFixed(2)}
      </td>
      <td className="px-3 py-2 align-top">
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={onEdit}
            title="Edit row"
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <Edit3 size={14} />
          </button>
          {showRelookup && (
            <button
              type="button"
              onClick={onRelookup}
              title="Re-run enrichment"
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <RefreshCw size={14} />
            </button>
          )}
          {!committed && (
            <button
              type="button"
              onClick={onDelete}
              title="Remove this row from the draft"
              className="rounded p-1 text-muted-foreground hover:bg-destructive/15 hover:text-destructive"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function EditRowModal({
  item,
  onClose,
  onSave,
}: {
  item: ApiImportDraftItem;
  onClose: () => void;
  onSave: (patch: Partial<ParsedShape>) => Promise<void>;
}) {
  const initial = item.parsed as ParsedShape;
  const [cardName, setCardName] = useState(initial.name ?? "");
  const [setNameVal, setSetName] = useState(initial.setName ?? "");
  const [setCode, setSetCode] = useState(initial.setCode ?? "");
  const [cardNumber, setCardNumber] = useState(initial.cardNumber ?? "");
  const [condition, setCondition] = useState(
    (initial.condition as CardCondition) ?? "NM",
  );
  const [quantity, setQuantity] = useState(String(initial.quantity ?? 1));
  const [marketPrice, setMarketPrice] = useState(
    String(initial.marketPrice ?? 0),
  );
  const [saving, setSaving] = useState(false);

  return (
    <Modal
      open
      onClose={onClose}
      ariaLabel="Edit import row"
      panelClassName="w-[min(640px,calc(100vw-2rem))] rounded-lg border border-border bg-card p-6"
    >
      <h2 className="text-sm font-semibold text-foreground">Edit row</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <Field
          label="Name"
          value={cardName}
          onChange={setCardName}
          className="sm:col-span-2"
        />
        <Field label="Set name" value={setNameVal} onChange={setSetName} />
        <Field
          label="Set code"
          value={setCode}
          onChange={setSetCode}
          placeholder="e.g. SV3 / 10E"
        />
        <Field
          label="Card number"
          value={cardNumber}
          onChange={setCardNumber}
        />
        <label className="block">
          <span className="text-xs font-medium text-muted-foreground">
            Condition
          </span>
          <select
            value={condition}
            onChange={(e) => setCondition(e.target.value as CardCondition)}
            className="mt-1 w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
          >
            {CONDITIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <Field
          label="Quantity"
          value={quantity}
          onChange={setQuantity}
          type="number"
        />
        <Field
          label="Market price ($)"
          value={marketPrice}
          onChange={setMarketPrice}
          type="number"
        />
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-border bg-card px-3 py-1.5 text-sm font-medium hover:bg-muted"
        >
          Cancel
        </button>
        <PrimaryButton
          onClick={async () => {
            setSaving(true);
            await onSave({
              name: cardName,
              setName: setNameVal,
              setCode,
              cardNumber,
              condition,
              quantity: parseInt(quantity, 10) || 1,
              marketPrice: parseFloat(marketPrice) || 0,
            });
            setSaving(false);
          }}
          disabled={saving}
          loading={saving}
          label={saving ? "Saving…" : "Save row"}
        />
      </div>
    </Modal>
  );
}

function Field({
  label,
  value,
  onChange,
  className,
  type = "text",
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  className?: string;
  type?: string;
  placeholder?: string;
}) {
  return (
    <label className={`block ${className ?? ""}`}>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
      />
    </label>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
      <AlertCircle size={16} className="mt-0.5 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

function ViewToggle({
  view,
  onChange,
}: {
  view: "grid" | "table";
  onChange: (next: "grid" | "table") => void;
}) {
  const Btn = ({
    value,
    icon,
    label,
  }: {
    value: "grid" | "table";
    icon: React.ReactNode;
    label: string;
  }) => {
    const active = view === value;
    return (
      <button
        type="button"
        onClick={() => onChange(value)}
        className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
          active
            ? "bg-card text-foreground shadow"
            : "text-muted-foreground hover:text-foreground"
        }`}
        aria-pressed={active}
        aria-label={`Show as ${label.toLowerCase()}`}
      >
        {icon}
        {label}
      </button>
    );
  };
  return (
    <div className="inline-flex items-center gap-0.5 rounded-md border border-border bg-muted/40 p-0.5">
      <Btn value="grid" icon={<Grid3x3 size={12} />} label="Grid" />
      <Btn value="table" icon={<Rows3 size={12} />} label="Table" />
    </div>
  );
}

/**
 * Tile renderer for one CSV row — Figma-style card-pair layout but fed
 * by the auto-fetched eBay PicURL images. Status pill on the header,
 * front + back thumbs with a swap glyph between them, and per-tile
 * Edit / Re-lookup / Delete actions. Committed view hides the
 * destructive actions.
 */
function ImportTile({
  item,
  index,
  committed,
  onEdit,
  onRelookup,
  onSwapImages,
  onDelete,
}: {
  item: ApiImportDraftItem;
  index: number;
  committed: boolean;
  onEdit: () => void;
  onRelookup: () => void;
  onSwapImages: () => void;
  onDelete: () => void;
}) {
  const p = item.parsed as ParsedShape;
  const front = item.imageFrontRef
    ? imageUrl(item.imageFrontRef, "thumb")
    : null;
  const back = item.imageBackRef ? imageUrl(item.imageBackRef, "thumb") : null;
  const showRelookup =
    item.enrichmentStatus === "failed" ||
    item.enrichmentStatus === "pending" ||
    (item.enrichmentStatus === "done" && !!item.lookupError);

  const matchComplete = !!(p.name && p.setName && p.cardNumber);

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-card p-3 transition-colors hover:border-border/80">
      {/* Header: index + status + actions */}
      <div className="flex items-center justify-between gap-1">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            #{index + 1}
          </span>
          <StatusPill status={item.enrichmentStatus} />
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          <button
            type="button"
            onClick={onEdit}
            title="Edit fields"
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <Edit3 size={13} />
          </button>
          {!committed && (
            <button
              type="button"
              onClick={onSwapImages}
              title="Swap front and back"
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <RefreshCw size={13} />
            </button>
          )}
          {showRelookup && (
            <button
              type="button"
              onClick={onRelookup}
              title="Re-run enrichment"
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <RefreshCw size={13} />
            </button>
          )}
          {!committed && (
            <button
              type="button"
              onClick={onDelete}
              title="Remove from draft"
              className="rounded p-1 text-muted-foreground hover:bg-destructive/15 hover:text-destructive"
            >
              <X size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Front + Back images — side-by-side, compact. No per-image
          labels (the Swap button in the header conveys the front/back
          relationship); natural width so object-cover doesn't crop. */}
      <div className="flex items-center justify-center gap-1.5">
        <TileImage src={front} />
        <TileImage src={back} />
      </div>

      {/* Footer: name + match summary + meta */}
      <div className="space-y-1">
        <div className="truncate text-sm font-semibold text-foreground">
          {p.name || "—"}
        </div>
        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
          {matchComplete ? (
            <CheckCircle2 size={10} className="text-success" />
          ) : (
            <AlertCircle size={10} className="text-warning" />
          )}
          <span className="truncate">
            {p.setName || "Unknown set"}
            {p.cardNumber ? ` · #${p.cardNumber}` : ""}
          </span>
        </div>
        <div className="flex items-center justify-between text-[11px]">
          <span className="text-muted-foreground">
            {p.condition || "NM"} · qty {p.quantity ?? 1}
          </span>
          <span className="font-mono font-medium text-foreground">
            ${(p.marketPrice ?? 0).toFixed(2)}
          </span>
        </div>
        {item.lookupError && (
          <div
            className="truncate text-[10px] text-warning"
            title={item.lookupError}
          >
            {item.lookupError}
          </div>
        )}
      </div>
    </div>
  );
}

function TileImage({ src }: { src: string | null }) {
  // Fixed 5:7 aspect — natural card ratio. No flex-1 so a wider tile
  // doesn't stretch the image and crop its top via object-cover.
  if (src) {
    return (
      <img
        src={src}
        alt=""
        className="h-[120px] w-[75px] rounded border border-border object-contain"
      />
    );
  }
  return (
    <div className="flex h-[120px] w-[75px] items-center justify-center rounded border border-dashed border-border text-muted-foreground/40">
      <ImageIcon size={14} />
    </div>
  );
}

/**
 * Sticky bottom Confirm bar — replaces the buried Confirm button in
 * the Assign + commit panel. Always visible on the review step so the
 * operator never has to scroll past 400 rows to find it.
 */
/**
 * TNK-140 phase B — sticky bottom on the Upload step. The actual
 * commit happens on /$id/review; this is just the handoff.
 */
function ContinueBar({
  rows,
  disabled,
  waitingForEnrichment,
  failedCount,
  onContinue,
}: {
  rows: number;
  disabled: boolean;
  waitingForEnrichment: boolean;
  failedCount: number;
  onContinue: () => void;
}) {
  const hint = waitingForEnrichment
    ? "Wait for enrichment to settle…"
    : failedCount > 0
      ? `${failedCount} failed — those rows land without photos`
      : "Photos look right? Move on to review.";
  return (
    /* TNK-165 follow-up: was `fixed inset-x-0 bottom-0`, which overlapped
       the bottom row of cards in the import grid (same issue we fixed on
       the review-step bar). `sticky bottom-0` lives in document flow,
       claims its own space at the end of the section, and still pins to
       the viewport bottom when the section is taller than the viewport.
       Negative margins span the full width of the parent's `px-4 md:px-6`
       so the bar looks edge-to-edge inside its column. */
    <div className="sticky inset-x-0 bottom-0 z-30 -mx-4 mt-4 border-t border-border bg-background/95 px-6 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.2)] backdrop-blur md:-mx-6">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
        <span style={{ color: FIGMA.muted, fontSize: 12 }}>{hint}</span>
        <button
          type="button"
          onClick={onContinue}
          disabled={disabled}
          className="inline-flex items-center gap-2 rounded-xl px-5 py-2.5 transition-all disabled:cursor-not-allowed disabled:opacity-50"
          style={{
            background: FIGMA.indigoGradient,
            border: "none",
            color: "#fff",
            fontFamily: FIGMA.fontBody,
            fontSize: 13,
            fontWeight: 600,
            boxShadow: FIGMA.indigoShadow,
          }}
          onMouseEnter={(e) => {
            if (!disabled)
              e.currentTarget.style.boxShadow = FIGMA.indigoShadowHover;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = FIGMA.indigoShadow;
          }}
        >
          <CheckCircle2 size={16} />
          Continue to Review · {rows} row{rows === 1 ? "" : "s"}
        </button>
      </div>
    </div>
  );
}
